/**
 * Utilitaires SEO pour la gestion dynamique des métadonnées
 */
import { generateSlug, generateActUrl } from './urlUtils';
import type { NgapAct } from '../types/ngap';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  twitterTitle: string;
  twitterDescription: string;
}

/**
 * Met à jour les métadonnées de la page
 */
export function updatePageMeta(letterKey?: string, customMeta?: Partial<PageMeta>, canonicalUrlOverride?: string): void {
  const meta = customMeta || generatePageMeta(letterKey);
  
  // Mettre à jour le titre
  document.title = meta.title;
  
  // Mettre à jour les meta tags
  updateMetaTag('description', meta.description);
  updateMetaTag('keywords', meta.keywords);
  
  // Mettre à jour les liens canoniques
  updateLinkTag('canonical', canonicalUrlOverride || meta.canonical);
  
  // Mettre à jour Open Graph
  updateMetaProperty('og:title', meta.ogTitle);
  updateMetaProperty('og:description', meta.ogDescription);
  updateMetaProperty('og:url', meta.ogUrl);
  
  // Mettre à jour Twitter
  updateMetaTag('twitter:title', meta.twitterTitle, 'name');
  updateMetaTag('twitter:description', meta.twitterDescription, 'name');
}

/**
 * Génère les métadonnées pour une page
 */
function generatePageMeta(letterKey?: string, customMeta?: Partial<PageMeta>): PageMeta {
  // Si des métadonnées personnalisées sont fournies, les utiliser
  if (customMeta) {
    return {
      title: customMeta.title || APP_INFO.TITLE,
      description: customMeta.description || APP_INFO.DESCRIPTION,
      keywords: customMeta.keywords || 'NGAP, kinésithérapie, cotations, tarifs, simulateur, revalorisation',
      canonical: customMeta.canonical || APP_INFO.BASE_URL,
      ogTitle: customMeta.ogTitle || customMeta.title || APP_INFO.SHORT_TITLE,
      ogDescription: customMeta.ogDescription || customMeta.description || APP_INFO.DESCRIPTION,
      ogUrl: customMeta.ogUrl || customMeta.canonical || APP_INFO.BASE_URL,
      twitterTitle: customMeta.twitterTitle || customMeta.title || APP_INFO.SHORT_TITLE,
      twitterDescription: customMeta.twitterDescription || customMeta.description || APP_INFO.DESCRIPTION
    };
  }
  
  const baseUrl = 'https://ngap-kine.fr';
  
  if (letterKey) {
    const normalizedKey = letterKey.toUpperCase();
    return {
      title: `Cotations ${normalizedKey} - NGAP Kinésithérapie | Tarifs et Revalorisations 2025`,
      description: `Découvrez toutes les cotations ${normalizedKey} pour kinésithérapeutes avec les tarifs actualisés, les revalorisations 2025-2027 et les conditions d'application selon le référentiel NGAP.`,
      keywords: `${normalizedKey}, cotation ${normalizedKey}, NGAP ${normalizedKey}, kinésithérapie, tarifs, revalorisation, physiothérapie, référentiel`,
      canonical: `${baseUrl}/cotations/${normalizedKey.toLowerCase()}`,
      ogTitle: `Cotations ${normalizedKey} - NGAP Kinésithérapie`,
      ogDescription: `Consultez les cotations ${normalizedKey} avec tarifs actualisés et revalorisations prévues pour kinésithérapeutes.`,
      ogUrl: `${baseUrl}/cotations/${normalizedKey.toLowerCase()}`,
      twitterTitle: `Cotations ${normalizedKey} - NGAP Kiné`,
      twitterDescription: `Tarifs ${normalizedKey} actualisés pour kinésithérapeutes avec revalorisations 2025-2027.`
    };
  }
  
  // Page d'accueil
  return {
    title: 'NGAP Kiné - Simulateur de cotations kinésithérapie | Tarifs 2025',
    description: 'Simulateur officiel des cotations NGAP pour kinésithérapeutes. Calculez vos tarifs, consultez les revalorisations 2025-2027 et optimisez vos consultations.',
    keywords: 'NGAP, kinésithérapie, cotations, tarifs, simulateur, revalorisation, AMK, AMS, AMC, RAM, RAO, RIM, RIC, VIM, VIC, VSM, VSC, physiothérapie',
    canonical: baseUrl,
    ogTitle: 'NGAP Kiné - Simulateur de cotations kinésithérapie',
    ogDescription: 'Simulateur officiel des cotations NGAP pour kinésithérapeutes. Calculez vos tarifs et consultez les revalorisations.',
    ogUrl: baseUrl,
    twitterTitle: 'NGAP Kiné - Simulateur de cotations',
    twitterDescription: 'Simulateur officiel NGAP pour kinésithérapeutes. Tarifs actualisés et revalorisations 2025-2027.'
  };
}

/**
 * Met à jour un meta tag
 */
function updateMetaTag(name: string, content: string, attribute: string = 'name'): void {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

/**
 * Met à jour une propriété Open Graph
 */
function updateMetaProperty(property: string, content: string): void {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}

/**
 * Met à jour un link tag
 */
function updateLinkTag(rel: string, href: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  link.href = href;
}

/**
 * Génère les métadonnées pour une page de hashtag
 */
export function generateHashtagPageMeta(hashtag: string, actsCount: number): PageMeta {
  const normalizedHashtag = hashtag.charAt(0).toUpperCase() + hashtag.slice(1).toLowerCase();
  const baseUrl = 'https://ngap-kine.fr';
  const hashtagSlug = generateSlug(hashtag);
  
  return {
    title: `${normalizedHashtag} - NGAP Kinésithérapie | ${actsCount} actes disponibles`,
    description: `Découvrez les ${actsCount} actes de kinésithérapie liés à ${normalizedHashtag.toLowerCase()}. Tarifs actualisés, revalorisations 2025-2027 et conditions d'application selon le référentiel NGAP.`,
    keywords: `${normalizedHashtag}, ${normalizedHashtag.toLowerCase()}, NGAP, kinésithérapie, pathologie, tarifs, revalorisation, physiothérapie, référentiel`,
    canonical: `${baseUrl}/cotations/hashtag/${hashtagSlug}`,
    ogTitle: `${normalizedHashtag} - NGAP Kinésithérapie (${actsCount} actes)`,
    ogDescription: `Consultez les ${actsCount} actes de kinésithérapie pour ${normalizedHashtag.toLowerCase()} avec tarifs actualisés et revalorisations prévues.`,
    ogUrl: `${baseUrl}/cotations/hashtag/${hashtagSlug}`,
    twitterTitle: `${normalizedHashtag} - NGAP Kiné`,
    twitterDescription: `${actsCount} actes de kinésithérapie pour ${normalizedHashtag.toLowerCase()} avec revalorisations 2025-2027.`
  };
}

/**
 * Génère les métadonnées pour une page de cotation spécifique avec date
 */
export function generateCotationValuePageMeta(
  act: NgapAct,
  cotationType: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027',
  cotationValue: string,
  cotationSlugValue: string
): PageMeta {
  const letterKey = act.lettre_cle?.toUpperCase() || '';
  const baseUrl = 'https://ngap-kine.fr';
  const canonicalUrl = `${baseUrl}${generateActUrl(act)}`;

  // Générer un suffixe pour le titre selon le type de cotation
  let dateSuffix = '';
  let statusText = '';
  const now = new Date();

  switch (cotationType) {
    case 'current':
      dateSuffix = '';
      statusText = 'Cotation actuelle';
      break;
    case 'janv2026':
      dateSuffix = ' - Janvier 2026';
      statusText = now >= new Date('2026-01-01') ? 'En vigueur depuis janvier 2026' : 'À venir en janvier 2026';
      break;
    case 'may2026':
      dateSuffix = ' - Mai 2026';
      statusText = now >= new Date('2026-05-01') ? 'En vigueur depuis mai 2026' : 'À venir en mai 2026';
      break;
    case 'sept2026':
      dateSuffix = ' - Septembre 2026';
      statusText = now >= new Date('2026-09-01') ? 'En vigueur depuis septembre 2026' : 'À venir en septembre 2026';
      break;
    case 'july2027':
      dateSuffix = ' - Juillet 2027';
      statusText = now >= new Date('2027-07-01') ? 'En vigueur depuis juillet 2027' : 'À venir en juillet 2027';
      break;
  }
  
  // Informations sur le référentiel
  const referentielText = act.soumis_referentiel ? ' Soumis au référentiel.' : ' Hors référentiel.';
  
  return {
    title: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix} | NGAP Kiné`,
    description: `${statusText} - Cotation ${letterKey} ${cotationValue} : ${act.libelle}.${referentielText} Tarifs métropole et DROM selon le référentiel NGAP.`,
    keywords: `${letterKey}, ${letterKey} ${cotationValue}, ${act.libelle}, NGAP, kinésithérapie, tarifs, cotation, ${act.theme || 'rééducation'}${dateSuffix ? `, revalorisation${dateSuffix.toLowerCase()}` : ''}`,
    canonical: canonicalUrl,
    ogTitle: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix} | NGAP Kiné`,
    ogDescription: `${statusText} - Cotation ${letterKey} ${cotationValue} : ${act.libelle}.${referentielText}`,
    ogUrl: `${baseUrl}/cotations/${cotationSlugValue}`,
    twitterTitle: `${letterKey} ${cotationValue} - ${act.libelle}${dateSuffix}`,
    twitterDescription: `${statusText} - Cotation ${letterKey} ${cotationValue} pour kinésithérapeutes.`
  };
}

/**
 * Injecte ou met à jour un script JSON-LD dans le DOM
 */
export function updateJsonLd(jsonLdContent: string, id: string = 'dynamic-jsonld'): void {
  let script = document.getElementById(id) as HTMLScriptElement;

  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = jsonLdContent;
}

/**
 * Supprime un script JSON-LD du DOM
 */
export function removeJsonLd(id: string = 'dynamic-jsonld'): void {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
  }
}

/**
 * Génère le JSON-LD Schema.org pour une page de cotation (lettre clé)
 */
export function generateCotationSchema(letterKey: string, actsCount?: number): string {
  const normalizedKey = letterKey.toUpperCase();

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": `Cotations ${normalizedKey} - NGAP Kinésithérapie`,
    "description": `Guide complet des cotations ${normalizedKey} pour kinésithérapeutes selon le référentiel NGAP officiel.${actsCount ? ` ${actsCount} actes disponibles.` : ''}`,
    "url": `https://ngap-kine.fr/cotations/actes/${normalizedKey.toLowerCase()}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "NGAP Kiné",
      "url": "https://ngap-kine.fr/"
    },
    "about": {
      "@type": "MedicalProcedure",
      "name": `Actes de kinésithérapie ${normalizedKey}`,
      "medicalSpecialty": {
        "@type": "MedicalSpecialty",
        "name": "Kinésithérapie"
      }
    },
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Kinésithérapeute"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Kinésithérapeutes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "App'Ines",
      "url": "https://www.appines.fr"
    },
    "inLanguage": "fr-FR",
    "dateModified": new Date().toISOString().split('T')[0]
  }, null, 2);
}

/**
 * Génère le JSON-LD Schema.org pour une page d'acte spécifique
 */
export function generateActSchema(
  act: NgapAct,
  cotationType?: 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): string {
  const letterKey = act.lettre_cle?.toUpperCase() || '';
  const cotationValue = cotationType && cotationType !== 'current'
    ? (act as any)[`cotation_valeur_${cotationType.replace('2026', '_2026').replace('2027', '_2027')}`] || act.cotation_valeur
    : act.cotation_valeur;

  const baseUrl = 'https://ngap-kine.fr';
  const actSlug = generateSlug(act.libelle || '');
  const actUrl = `${baseUrl}/cotations/${letterKey.toLowerCase()}/${actSlug}`;

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": `${letterKey} ${cotationValue} - ${act.libelle}`,
    "description": act.libelle || '',
    "url": actUrl,
    "isPartOf": {
      "@type": "WebSite",
      "name": "NGAP Kiné",
      "url": "https://ngap-kine.fr/"
    },
    "about": {
      "@type": "MedicalProcedure",
      "name": act.libelle || '',
      "code": {
        "@type": "MedicalCode",
        "code": `${letterKey} ${cotationValue}`,
        "codingSystem": "NGAP"
      },
      "medicalSpecialty": {
        "@type": "MedicalSpecialty",
        "name": "Kinésithérapie"
      }
    },
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Kinésithérapeute"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Kinésithérapeutes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "App'Ines",
      "url": "https://www.appines.fr"
    },
    "inLanguage": "fr-FR",
    "dateModified": new Date().toISOString().split('T')[0]
  }, null, 2);
}

/**
 * Génère le JSON-LD Schema.org pour une page de hashtag/pathologie
 */
export function generateHashtagSchema(hashtag: string, acts: NgapAct[]): string {
  const normalizedHashtag = hashtag.charAt(0).toUpperCase() + hashtag.slice(1).toLowerCase();
  const hashtagSlug = generateSlug(hashtag);
  const baseUrl = 'https://ngap-kine.fr';

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": `${normalizedHashtag} - NGAP Kinésithérapie`,
    "description": `Actes de kinésithérapie pour ${normalizedHashtag.toLowerCase()}. ${acts.length} actes disponibles selon le référentiel NGAP.`,
    "url": `${baseUrl}/cotations/hashtag/${hashtagSlug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "NGAP Kiné",
      "url": "https://ngap-kine.fr/"
    },
    "about": {
      "@type": "MedicalCondition",
      "name": normalizedHashtag,
      "associatedAnatomy": {
        "@type": "AnatomicalStructure",
        "name": normalizedHashtag
      }
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": acts.slice(0, 10).map((act, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "MedicalProcedure",
          "name": `${act.lettre_cle} ${act.cotation_valeur} - ${act.libelle}`,
          "code": {
            "@type": "MedicalCode",
            "code": `${act.lettre_cle} ${act.cotation_valeur}`,
            "codingSystem": "NGAP"
          }
        }
      }))
    },
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Kinésithérapeute"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Kinésithérapeutes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "App'Ines",
      "url": "https://www.appines.fr"
    },
    "inLanguage": "fr-FR",
    "dateModified": new Date().toISOString().split('T')[0]
  }, null, 2);
}

/**
 * Génère le JSON-LD Schema.org pour une page de thème
 */
export function generateThemeSchema(theme: string, acts: NgapAct[]): string {
  const normalizedTheme = theme.charAt(0).toUpperCase() + theme.slice(1).toLowerCase();
  const themeSlug = generateSlug(theme);
  const baseUrl = 'https://ngap-kine.fr';

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": `${normalizedTheme} - NGAP Kinésithérapie`,
    "description": `Actes de ${normalizedTheme.toLowerCase()} pour kinésithérapeutes. ${acts.length} actes disponibles selon le référentiel NGAP.`,
    "url": `${baseUrl}/themes/${themeSlug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "NGAP Kiné",
      "url": "https://ngap-kine.fr/"
    },
    "about": {
      "@type": "MedicalSpecialty",
      "name": normalizedTheme
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": acts.slice(0, 10).map((act, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "MedicalProcedure",
          "name": `${act.lettre_cle} ${act.cotation_valeur} - ${act.libelle}`,
          "code": {
            "@type": "MedicalCode",
            "code": `${act.lettre_cle} ${act.cotation_valeur}`,
            "codingSystem": "NGAP"
          }
        }
      }))
    },
    "medicalAudience": {
      "@type": "MedicalAudience",
      "audienceType": "Kinésithérapeute"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Kinésithérapeutes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "App'Ines",
      "url": "https://www.appines.fr"
    },
    "inLanguage": "fr-FR",
    "dateModified": new Date().toISOString().split('T')[0]
  }, null, 2);
}

/**
 * Génère le JSON-LD Schema.org pour la page simulateur
 */
export function generateSimulatorSchema(): string {
  const baseUrl = 'https://ngap-kine.fr';

  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Simulateur de Revenus NGAP Kiné",
    "description": "Simulateur de revenus pour kinésithérapeutes basé sur les cotations NGAP. Calculez vos revenus prévisionnels avec les tarifs actualisés et les revalorisations 2025-2027.",
    "url": `${baseUrl}/simulateur`,
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Web Browser",
    "browserRequirements": "Requires JavaScript",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "NGAP Kiné",
      "url": "https://ngap-kine.fr/"
    },
    "featureList": [
      "Calcul de revenus prévisionnels",
      "Simulation sur différentes périodes",
      "Prise en compte des majorations",
      "Visualisation graphique des revenus",
      "Export des résultats"
    ],
    "creator": {
      "@type": "Organization",
      "name": "App'Ines",
      "url": "https://www.appines.fr"
    },
    "audience": {
      "@type": "ProfessionalAudience",
      "audienceType": "Kinésithérapeutes"
    },
    "inLanguage": "fr-FR",
    "dateModified": new Date().toISOString().split('T')[0],
    "isAccessibleForFree": true
  }, null, 2);
}